import React, {useEffect, useState} from 'react'
import axios from 'axios'
import {QRCodeCanvas} from 'qrcode.react'
import {useParams} from 'react-router-dom'
import {jsPDF} from 'jspdf'
import autoTable from 'jspdf-autotable'

const API_URL = process.env.REACT_APP_API_URL
const GET_SCHEDULE_BY_CLASS_URL = `${API_URL}/Custom/GetUniversityWeeklyScheduleByClass`
const GET_SCHEDULE_BY_CLASSROOM_URL = `${API_URL}/Custom/GetUniversityWeeklyScheduleByClassroom`
const GET_SCHEDULE_BY_INSTRUCTOR_URL = `${API_URL}/Custom/GetUniversityWeeklyScheduleByInstructor`
const GET_SEMESTERS_URL = `${API_URL}/Custom/GetSemesters`
const GET_FACULTIES_URL = `${API_URL}/Custom/GetFaculties`
const GET_DEPARTMENTS_URL = `${API_URL}/Custom/GetDepartmentsByFaculty`
const GET_INSTRUCTORS_URL = `${API_URL}/Custom/GetAllInstructors`

interface ScheduleItem {
  id: number
  day: number
  startTime: string
  endTime: string
  courseName: string
  courseCode?: string
  classroomName: string
  name?: string
  detail?: string
  isEvent?: boolean
  eventDate?: string | null
}

interface Option {
  id: number
  name: string
}

const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar']
const toDayKey = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
const getCurrentWeekRange = () => {
  const now = new Date()
  const dayOfWeek = (now.getDay() + 6) % 7
  const monday = new Date(now)
  monday.setDate(now.getDate() - dayOfWeek)
  monday.setHours(0, 0, 0, 0)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  return {start: toDayKey(monday), end: toDayKey(sunday)}
}
const parseDateInput = (value: string) => {
  if (!value) return null
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null
  return date
}
const formatHeaderDate = (value: string) => {
  const date = parseDateInput(value)
  if (!date) return value
  const dayName = dayNames[(date.getDay() + 6) % 7]
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year} ${dayName}`
}

const PublicUniversityCourseSchedulePrint: React.FC = () => {
  const {semesterId, departmentId, classId, classroomId, instructorId} = useParams()
  const [schedule, setSchedule] = useState<ScheduleItem[]>([])
  const [semesterName, setSemesterName] = useState<string>('')
  const [facultyName, setFacultyName] = useState<string>('')
  const [departmentName, setDepartmentName] = useState<string>('')
  const [instructorName, setInstructorName] = useState<string>('')
  const [rangeStart, setRangeStart] = useState<string>('')
  const [rangeEnd, setRangeEnd] = useState<string>('')

  const isInstructorMode = Boolean(instructorId)
  const isClassroomMode = Boolean(classroomId)
  const displayClassroomName = schedule.find((s) => s.classroomName)?.classroomName || ''
  const className = classId ? `${classId}. Sınıf` : ''
  const headerText = isInstructorMode
    ? `${semesterName || semesterId || '....'} ${instructorName || '...'} öğretim elemanı ders programı`
    : isClassroomMode
    ? `${semesterName || semesterId || '....'} ${displayClassroomName || '...'} numaralı derslik ders programı`
    : `${semesterName || semesterId || '....'} ${className || '...'} sınıfı ders programı`

  useEffect(() => {
    const currentWeek = getCurrentWeekRange()
    const params = new URLSearchParams(window.location.search)
    const urlStart = params.get('start') || ''
    const urlEnd = params.get('end') || ''
    setRangeStart(urlStart || currentWeek.start)
    setRangeEnd(urlEnd || currentWeek.end)
  }, [])

  useEffect(() => {
    if (!semesterId || !instructorId || !isInstructorMode) {
      setInstructorName('')
      return
    }
    axios
      .get(`${GET_INSTRUCTORS_URL}?semesterId=${semesterId}`)
      .then((res) => {
        const instructors: Option[] = res.data || []
        const matched = instructors.find((i) => String(i.id) === instructorId)
        setInstructorName(matched?.name || '')
      })
      .catch(console.error)
  }, [semesterId, instructorId, isInstructorMode])

  useEffect(() => {
    if (!semesterId) return
    axios
      .get(GET_SEMESTERS_URL)
      .then((res) => {
        const semesters: Option[] = res.data || []
        const matched = semesters.find((s) => String(s.id) === semesterId)
        setSemesterName(matched?.name || '')
      })
      .catch(console.error)
  }, [semesterId])

  useEffect(() => {
    if (!departmentId || isInstructorMode || isClassroomMode) {
      setFacultyName('')
      setDepartmentName('')
      return
    }

    axios
      .get(GET_FACULTIES_URL)
      .then(async (res) => {
        const faculties: Option[] = res.data || []
        let selectedFacultyId: number | null = null
        let selectedDepartmentName = ''

        for (const faculty of faculties) {
          try {
            const depRes = await axios.get(`${GET_DEPARTMENTS_URL}/${faculty.id}`)
            const departments: Option[] = depRes.data || []
            const matchedDepartment = departments.find((d) => String(d.id) === departmentId)
            if (matchedDepartment) {
              selectedFacultyId = faculty.id
              selectedDepartmentName = matchedDepartment.name
              break
            }
          } catch (error) {
            console.error(error)
          }
        }

        const matchedFaculty = faculties.find((f) => f.id === selectedFacultyId)
        setFacultyName(matchedFaculty?.name || '')
        setDepartmentName(selectedDepartmentName)
      })
      .catch(console.error)
  }, [departmentId, isInstructorMode, isClassroomMode])

  useEffect(() => {
    if (!semesterId) return

    if (isInstructorMode) {
      if (!instructorId) return
      axios
        .get(`${GET_SCHEDULE_BY_INSTRUCTOR_URL}?semesterId=${semesterId}&instructorId=${instructorId}`)
        .then((res) =>
          setSchedule(
            (res.data || []).map((i: any) => ({...i, eventDate: i.eventDate || i.EventDate || null}))
          )
        )
        .catch(console.error)
      return
    }

    if (isClassroomMode) {
      if (!classroomId) return
      axios
        .get(`${GET_SCHEDULE_BY_CLASSROOM_URL}?semesterId=${semesterId}&classroomId=${classroomId}`)
        .then((res) =>
          setSchedule(
            (res.data || []).map((i: any) => ({...i, eventDate: i.eventDate || i.EventDate || null}))
          )
        )
        .catch(console.error)
      return
    }

    if (!departmentId || !classId) return
    axios
      .get(
        `${GET_SCHEDULE_BY_CLASS_URL}?semesterId=${semesterId}&classId=${classId}&departmentId=${departmentId}`
      )
      .then((res) =>
        setSchedule(
          (res.data || []).map((i: any) => ({...i, eventDate: i.eventDate || i.EventDate || null}))
        )
      )
      .catch(console.error)
  }, [semesterId, departmentId, classId, classroomId, instructorId, isClassroomMode, isInstructorMode])

  const getDateRangeKeys = () => {
    const start = parseDateInput(rangeStart)
    const end = parseDateInput(rangeEnd)
    if (!start || !end || start > end) return []
    const result: string[] = []
    const cursor = new Date(start)
    while (cursor <= end) {
      result.push(toDayKey(cursor))
      cursor.setDate(cursor.getDate() + 1)
    }
    return result
  }
  const dateKeys = getDateRangeKeys()

  const getTimeSlots = () =>
    Array.from(
      new Set(
        schedule
          .filter((s) => {
            if (!dateKeys.length) return false
            if (s.eventDate) {
              const eventDate = (s.eventDate || '').split('T')[0]
              return dateKeys.includes(eventDate)
            }
            const day = s.day
            return dateKeys.some((dateKey) => {
              const date = parseDateInput(dateKey)
              if (!date) return false
              const dow = ((date.getDay() + 6) % 7) + 1
              return day === dow
            })
          })
          .map((s) => `${s.startTime} - ${s.endTime}`)
      )
    ).sort((a, b) => a.localeCompare(b))

  const getSlotMap = () =>
    schedule.reduce((acc: Record<string, ScheduleItem[]>, item) => {
      if (!dateKeys.length) return acc
      const addItemToDate = (dateKey: string) => {
        const key = `${dateKey}_${item.startTime}-${item.endTime}`
        if (!acc[key]) acc[key] = []
        acc[key].push(item)
      }
      if (item.eventDate) {
        const eventDate = (item.eventDate || '').split('T')[0]
        if (dateKeys.includes(eventDate)) {
          addItemToDate(eventDate)
        }
        return acc
      }
      dateKeys.forEach((dateKey) => {
        const date = parseDateInput(dateKey)
        if (!date) return
        const dow = ((date.getDay() + 6) % 7) + 1
        if (item.day === dow) {
          addItemToDate(dateKey)
        }
      })
      return acc
    }, {})

  const renderTable = () => {
    if (!schedule.length) return null
    const timeSlots = getTimeSlots()
    const slotMap = getSlotMap()

    return (
      <div className='table-responsive schedule-table-wrapper'>
        <table className='table table-bordered schedule-print-table'>
          <thead>
            <tr>
              <th className='text-center align-middle time-slot-column'>Saat</th>
              {dateKeys.map((dateKey) => (
                <th key={dateKey} className='text-center align-middle day-column'>
                  {formatHeaderDate(dateKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map((slot) => {
              const [startTime = '', endTime = ''] = slot.split(' - ')
              return (
                <tr key={slot}>
                  <td className='align-middle text-center time-slot-cell'>{slot}</td>
                  {dateKeys.map((dateKey) => {
                    const rowKey = `${dateKey}_${startTime}-${endTime}`
                    const items = slotMap[rowKey] || []
                    return (
                      <td key={rowKey} className='schedule-cell'>
                        {items.map((i) => (
                          <div key={i.id} className='schedule-cell-item'>
                            <div className='fw-bold text-truncate'>
                              {i.courseCode ? `${i.courseName} (${i.courseCode})` : i.courseName}
                            </div>
                            <div className='text-truncate'>{i.classroomName}</div>
                            <div className='text-truncate'>{i.name ? i.name.split(' - ').pop() : ''}</div>
                          </div>
                        ))}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  const downloadScheduleAsPdf = () => {
    const dayHeaders = ['Saat', ...dateKeys.map((dateKey) => formatHeaderDate(dateKey))]
    const timeSlots = getTimeSlots()
    const slotMap = getSlotMap()

    const body = timeSlots.map((slot) => {
      const [startTime = '', endTime = ''] = slot.split(' - ')
      const dayCells = dateKeys.map((dateKey) => {
        const rowKey = `${dateKey}_${startTime}-${endTime}`
        const items = slotMap[rowKey] || []
        return items
          .map((item) => {
            const lesson = item.courseCode ? `${item.courseName} (${item.courseCode})` : item.courseName
            const type = item.name ? item.name.split(' - ').pop() : ''
            return [lesson, item.classroomName, type].filter(Boolean).join(' | ')
          })
          .join(' / ')
      })

      return [slot, ...dayCells]
    })

    const doc = new jsPDF({orientation: 'landscape', unit: 'pt', format: 'a4'})
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 28
    const headerY = 28
    const qrSize = 56

    doc.setFontSize(11)
    doc.text(headerText, margin, headerY)

    const qrContainer = document.getElementById('program-print-qr') as HTMLCanvasElement | null
    if (qrContainer) {
      try {
        const dataUrl = qrContainer.toDataURL('image/png')
        doc.addImage(dataUrl, 'PNG', pageWidth - margin - qrSize, headerY - 12, qrSize, qrSize)
      } catch (error) {
        console.error(error)
      }
    }

    const tableTop = 54
    const footerArea = 26
    const availableHeight = pageHeight - tableTop - footerArea - margin
    const minCellHeight = body.length > 0 ? Math.max(18, Math.floor(availableHeight / body.length) - 2) : 20

    autoTable(doc, {
      head: [dayHeaders],
      body,
      startY: tableTop,
      margin: {left: margin, right: margin, top: tableTop, bottom: margin + footerArea},
      theme: 'grid',
      styles: {
        fontSize: 6.5,
        cellPadding: 2,
        overflow: 'ellipsize',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [52, 58, 64],
        fontSize: 7,
      },
      columnStyles: {
        0: {cellWidth: 64},
      },
      didParseCell: (data: any) => {
        data.cell.styles.minCellHeight = minCellHeight
      },
      rowPageBreak: 'avoid',
    })

    doc.setFontSize(8)
    doc.text(
      'Not: Dersliklerin canlı durumlarını QR kodu okuyarak takip edebilirsiniz.',
      margin,
      pageHeight - 14
    )

    const fileName = isInstructorMode
      ? `OgretimElemani_DersProgrami_${(semesterName || semesterId || 'donem').toString().replace(/\s+/g, '_')}_${(instructorName || instructorId || 'ogretim_elemani').toString().replace(/\s+/g, '_')}.pdf`
      : isClassroomMode
      ? `Derslik_DersProgrami_${(semesterName || semesterId || 'donem').toString().replace(/\s+/g, '_')}_${(displayClassroomName || classroomId || 'derslik').toString().replace(/\s+/g, '_')}.pdf`
      : `DersProgrami_${(semesterName || semesterId || 'donem').toString().replace(/\s+/g, '_')}_${(className || classId || 'sinif').toString().replace(/\s+/g, '_')}.pdf`

    doc.save(fileName)
  }

  const currentUrl = isInstructorMode
    ? `${window.location.origin}/program/print-instructor/${semesterId}/${instructorId}`
    : isClassroomMode
    ? `${window.location.origin}/program/print-classroom/${semesterId}/${classroomId}`
    : `${window.location.origin}/program/print/${semesterId}/${departmentId}/${classId}`

  return (
    <div className='container mt-3 print-page-wrapper'>
      <style>{`
        .print-page-wrapper {
          max-width: 100%;
        }
        .schedule-print-table {
          table-layout: fixed;
          width: 100%;
        }
        .schedule-print-table th,
        .schedule-print-table td {
          border-color: #8b8b8b !important;
          font-size: 10px;
          line-height: 1.15;
          padding: 2px 3px;
        }
        .schedule-table-wrapper {
          max-height: calc(100vh - 220px);
          overflow: visible;
        }
        .time-slot-column {
          width: 86px;
          min-width: 86px;
        }
        .day-column {
          min-width: 102px;
        }
        .time-slot-cell {
          height: 44px;
          min-height: 44px;
          font-weight: 600;
          background: #f8f9fa;
        }
        .schedule-cell {
          height: 44px;
          min-height: 44px;
          vertical-align: middle;
        }
        .schedule-cell-item {
          margin-bottom: 2px;
          max-width: 100%;
        }
        .page-header-title {
          font-size: 14px;
          line-height: 1.25;
          margin: 0;
          font-weight: 700;
        }
        .print-note {
          font-size: 12px;
          margin-top: 8px;
        }
        @media print {
          @page {
            size: A4 landscape;
            margin: 6mm;
          }
          html,
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            width: 297mm;
            height: 210mm;
            overflow: hidden;
          }
          .print-actions {
            display: none !important;
          }
          .print-page-wrapper {
            margin-top: 0 !important;
          }
          .page-header-title {
            font-size: 12px;
          }
          .schedule-print-table th,
          .schedule-print-table td {
            font-size: 8.5px;
          }
          .time-slot-cell,
          .schedule-cell {
            height: 40px;
            min-height: 40px;
          }
        }
      `}</style>

      <div className='d-flex justify-content-between align-items-start mb-2'>
        <h2 className='page-header-title'>{headerText}</h2>
        <QRCodeCanvas id='program-print-qr' value={currentUrl} size={80} />
      </div>

      <div className='row g-2 mb-3 print-actions'>
        <div className='col-12 col-md-3'>
          <label className='form-label mb-1'>Başlangıç Tarihi</label>
          <input
            type='date'
            className='form-control form-control-sm'
            value={rangeStart}
            onChange={(e) => setRangeStart(e.target.value)}
          />
        </div>
        <div className='col-12 col-md-3'>
          <label className='form-label mb-1'>Bitiş Tarihi</label>
          <input
            type='date'
            className='form-control form-control-sm'
            value={rangeEnd}
            onChange={(e) => setRangeEnd(e.target.value)}
          />
        </div>
      </div>

      {!isClassroomMode && !isInstructorMode && (
        <div className='text-muted mb-2' style={{fontSize: '12px'}}>
          {facultyName && `${facultyName} / `}
          {departmentName && `${departmentName} / `}
          {className}
        </div>
      )}

      {isClassroomMode && displayClassroomName && (
        <div className='text-muted mb-2' style={{fontSize: '12px'}}>
          {displayClassroomName}
        </div>
      )}
      {isInstructorMode && (
        <div className='text-muted mb-2' style={{fontSize: '12px'}}>
          {instructorName || instructorId}
        </div>
      )}

      {dateKeys.length === 0 ? (
        <div className='alert alert-warning'>Lütfen geçerli bir tarih aralığı seçiniz.</div>
      ) : (
        renderTable()
      )}

      <div className='print-note'>
        Not: Dersliklerin canlı durumlarını QR kodu okuyarak takip edebilirsiniz.
      </div>

      <div className='d-flex justify-content-end gap-2 mt-3 print-actions'>
        <button className='btn btn-light-primary' onClick={downloadScheduleAsPdf}>
          PDF Yazdır
        </button>
        <button className='btn btn-primary' onClick={() => window.print()}>
          Yazdır (A4 Yatay)
        </button>
      </div>
    </div>
  )
}

export default PublicUniversityCourseSchedulePrint
